import produce from "immer";
import create, { GetState, SetState, State, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";

import { isDev } from "@ucchub/ui";

import { roleManagementStore } from "./roleManagementStore/roleManagementStore";
import { CombinedStore } from "./useCombinedStore.types";
import { userManagementStore } from "./userManagementStore/userManagementStore";

// Immer middleware. Shortcuts the use of immer for immutable state management
const immer =
  <T extends State>(config: StateCreator<T>): StateCreator<T> =>
  (set, get, api) =>
    config(
      (partial, replace) => {
        const nextState =
          typeof partial === "function"
            ? produce(partial as (state: T) => T)
            : (partial as T);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return set(nextState as any, replace);
      },
      get,
      api
    );

// We pull all of the store definitions into this file and create a single combined store.
// Having a single combined store is more scalable, as it allows all of the stores to
// communicate with one another.
const combinedStore = (
  set: SetState<CombinedStore>,
  get: GetState<CombinedStore>
) => {
  return {
    ...userManagementStore(set, get),
    ...roleManagementStore(set, get),
  };
};

export const useCombinedStore = !isDev()
  ? create<CombinedStore>(immer(combinedStore))
  : create<CombinedStore>(
      devtools(immer(combinedStore), { name: "Combined Store" })
    );
